import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../services/firebase';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  MultiFactorInfo,
  MultiFactorResolver
} from 'firebase/auth';
import { Loader2, Eye, EyeOff, Check, ArrowLeft, Mail, Lock, CheckCircle2, X } from 'lucide-react';
import { PrivacyModal } from './PrivacyModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import PwaInstallControl from './PwaInstallControl';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { useModalAccessibility } from '../hooks/useModalAccessibility';

type ViewState = 'landing' | 'login' | 'register';

// --- COMPONENTE EXTRAÍDO ---
interface HeaderCurveProps {
  title?: string;
  subtitle?: string;
  showBack?: boolean;
  onBack?: () => void;
  isLanding: boolean;
}

const HeaderCurve: React.FC<HeaderCurveProps> = ({ title, subtitle, showBack = false, onBack, isLanding }) => (
  <div className="relative w-full bg-panel pb-6 pt-[calc(2rem+env(safe-area-inset-top,0px))] md:pt-6 md:pb-8 lg:pt-7 lg:pb-7 rounded-b-[30px] md:rounded-b-[50px] lg:rounded-b-[36px] px-6 lg:px-10 flex flex-col items-center justify-center text-center shadow-sm z-0 flex-shrink-0 border-b border-line">


    {showBack && onBack && (
      <button
        onClick={onBack}
        type="button"
        aria-label="Volver al inicio"
        className="absolute top-[calc(1.5rem+env(safe-area-inset-top,0px))] lg:top-5 left-6 w-11 h-11 flex items-center justify-center bg-panel/60 hover:bg-panel rounded-full text-ink transition-all shadow-sm backdrop-blur-sm"
      >
        <ArrowLeft size={20} />
      </button>
    )}

    <div className={`relative mb-4 md:mb-5 lg:mb-4 ${isLanding ? 'w-28 h-28 lg:w-24 lg:h-24' : 'w-20 h-20 lg:w-16 lg:h-16'} transition-all duration-500`}>
      <img src="/juris-logo.png" alt="Juris" width={112} height={112} className="w-full h-full object-contain drop-shadow-lg" />
    </div>

    {isLanding ? (
      <div key="landing-header-copy" className="motion-fade-through">
        <h1 className="text-3xl md:text-3xl font-extrabold text-ink mb-1 tracking-tight">JURIS</h1>
        <p className="text-juris font-medium text-base md:text-lg lg:text-base leading-relaxed max-w-xs mx-auto">Tu asistente legal operativo e inteligente</p>
      </div>
    ) : (
      <div key={`${title}-${subtitle}`} className="motion-fade-through">
        <h2 className="text-xl md:text-2xl lg:text-xl font-bold text-ink mb-1">{title}</h2>
        {subtitle && <p className="text-juris text-xs md:text-sm max-w-[280px] mx-auto leading-relaxed">{subtitle}</p>}
      </div>
    )}
  </div>
);

// --- COMPONENTE PRINCIPAL ---
const Login: React.FC = () => {
  const { isStandalone } = usePwaInstall();
  const MFA_CODE_TTL_SECONDS = 120;
  const MFA_RESEND_COOLDOWN_SECONDS = 30;
  const MFA_MAX_FAILURES_BEFORE_RESEND = 3;

  const [view, setView] = useState<ViewState>('landing');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaStep, setMfaStep] = useState<'select' | 'code'>('select');
  const [mfaHints, setMfaHints] = useState<MultiFactorInfo[]>([]);
  const [mfaSelectedHint, setMfaSelectedHint] = useState<MultiFactorInfo | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaExpiresAt, setMfaExpiresAt] = useState<number | null>(null);
  const [mfaRemainingSeconds, setMfaRemainingSeconds] = useState(0);
  const [mfaCooldownEndsAt, setMfaCooldownEndsAt] = useState<number | null>(null);
  const [mfaCooldownSeconds, setMfaCooldownSeconds] = useState(0);
  const [mfaFailedAttempts, setMfaFailedAttempts] = useState(0);
  const [recaptchaKey, setRecaptchaKey] = useState(0);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const recaptchaElementRef = useRef<HTMLDivElement | null>(null);
  const mfaDialogRef = useRef<HTMLDivElement>(null);
  const deleteSuccessDialogRef = useRef<HTMLDivElement>(null);

  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  useEffect(() => {
    const wasDeleted = localStorage.getItem('juris_account_deleted');
    if (wasDeleted) {
      setShowDeleteSuccess(true);
      localStorage.removeItem('juris_account_deleted');
      setView('landing');
    }
  }, []);

  useEffect(() => {
    return () => {
      resetRecaptcha(false);
    };
  }, []);

  useEffect(() => {
    if (!mfaOpen) {
      setMfaRemainingSeconds(0);
      setMfaCooldownSeconds(0);
      return;
    }

    const updateTimers = () => {
      const now = Date.now();
      if (mfaExpiresAt) {
        const remaining = Math.max(0, Math.ceil((mfaExpiresAt - now) / 1000));
        setMfaRemainingSeconds(remaining);
      } else {
        setMfaRemainingSeconds(0);
      }

      if (mfaCooldownEndsAt) {
        const remaining = Math.max(0, Math.ceil((mfaCooldownEndsAt - now) / 1000));
        setMfaCooldownSeconds(remaining);
      } else {
        setMfaCooldownSeconds(0);
      }
    };

    updateTimers();
    const intervalId = window.setInterval(updateTimers, 1000);
    return () => window.clearInterval(intervalId);
  }, [mfaOpen, mfaExpiresAt, mfaCooldownEndsAt]);

  const resetForm = () => {
    setError('');
    setPassword('');
    setLoading(false);
  };

  const resetRecaptcha = (refreshContainer = true) => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    recaptchaElementRef.current = null;
    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.innerHTML = '';
    }
    if (refreshContainer) {
      setRecaptchaKey((prev) => prev + 1);
    }
  };

  const resetMfaState = () => {
    setMfaOpen(false);
    setMfaStep('select');
    setMfaHints([]);
    setMfaSelectedHint(null);
    setMfaResolver(null);
    setMfaVerificationId('');
    setMfaCode('');
    setMfaError('');
    setMfaLoading(false);
    setMfaExpiresAt(null);
    setMfaRemainingSeconds(0);
    setMfaCooldownEndsAt(null);
    setMfaCooldownSeconds(0);
    setMfaFailedAttempts(0);
    resetRecaptcha();
  };

  const maskPhoneNumber = (hint: MultiFactorInfo | null) => {
    const phone = (hint as { phoneNumber?: string })?.phoneNumber || '';
    if (!phone) return '***';
    const last = phone.slice(-4);
    return `***${last}`;
  };

  const formatCountdown = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const ensureRecaptchaVerifier = () => {
    if (!recaptchaContainerRef.current) {
      throw new Error('recaptcha-container-missing');
    }
    if (
      recaptchaVerifierRef.current &&
      recaptchaElementRef.current &&
      recaptchaElementRef.current === recaptchaContainerRef.current
    ) {
      return recaptchaVerifierRef.current;
    }
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
      recaptchaElementRef.current = null;
    }
    if (recaptchaContainerRef.current.innerHTML) {
      recaptchaContainerRef.current.innerHTML = '';
    }
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current, { size: 'invisible' });
    recaptchaVerifierRef.current = verifier;
    recaptchaElementRef.current = recaptchaContainerRef.current;
    return verifier;
  };

  const startMfaFlow = async (resolver: MultiFactorResolver) => {
    const hints = resolver.hints || [];
    setMfaResolver(resolver);
    setMfaHints(hints);
    setMfaSelectedHint(hints[0] || null);
    setMfaVerificationId('');
    setMfaCode('');
    setMfaError('');
    setMfaFailedAttempts(0);
    setMfaExpiresAt(null);
    setMfaRemainingSeconds(0);
    setMfaCooldownEndsAt(null);
    setMfaCooldownSeconds(0);
    setMfaOpen(true);
    setMfaStep(hints.length > 1 ? 'select' : 'code');

    if (hints.length === 1) {
      await sendMfaCode('initial', resolver, hints[0]);
    }
  };

  const sendMfaCode = async (
    reason: 'initial' | 'resend',
    resolverOverride?: MultiFactorResolver,
    hintOverride?: MultiFactorInfo
  ) => {
    const resolverToUse = resolverOverride || mfaResolver;
    const hintToUse = hintOverride || mfaSelectedHint;

    if (!resolverToUse || !hintToUse) {
      setMfaError('No se pudo completar la verificación. Inténtalo de nuevo.');
      return;
    }

    if (!recaptchaContainerRef.current) {
      setMfaError('No se pudo verificar el envío del SMS. Recarga la página e inténtalo.');
      resetRecaptcha();
      return;
    }

    if (reason === 'resend') {
      setMfaCode('');
      setMfaError('');
      setMfaFailedAttempts(0);
    }

    setMfaLoading(true);
    setMfaError('');

    try {
      const verifier = ensureRecaptchaVerifier();
      const phoneProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneProvider.verifyPhoneNumber(
        { multiFactorHint: hintToUse, session: resolverToUse.session },
        verifier
      );

      const now = Date.now();
      setMfaVerificationId(verificationId);
      setMfaStep('code');
      setMfaCode('');
      setMfaFailedAttempts(0);
      setMfaExpiresAt(now + MFA_CODE_TTL_SECONDS * 1000);
      setMfaRemainingSeconds(MFA_CODE_TTL_SECONDS);
      setMfaCooldownEndsAt(now + MFA_RESEND_COOLDOWN_SECONDS * 1000);
      setMfaCooldownSeconds(MFA_RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      const code = err?.code || 'unknown';
      if (code === 'auth/captcha-check-failed' || code === 'auth/invalid-app-credential') {
        console.debug('[MFA] verifyPhoneNumber failed', err);
        setMfaError('No se pudo verificar el envío del SMS. Recarga la página e inténtalo.');
        resetRecaptcha();
      } else if (code === 'auth/too-many-requests') {
        console.debug('[MFA] verifyPhoneNumber failed', err);
        setMfaError('Demasiados intentos. Espera unos minutos e inténtalo de nuevo.');
      } else if (code === 'auth/session-expired' || code === 'auth/code-expired' || code === 'auth/timeout') {
        console.debug('[MFA] verifyPhoneNumber failed', err);
        setMfaError('El código ha caducado o ya no es válido. Solicita uno nuevo.');
      } else {
        console.error('[MFA] verifyPhoneNumber failed', err);
        setMfaError('No se pudo completar la verificación. Inténtalo de nuevo.');
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyMfaCode = async () => {
    if (!mfaResolver || !mfaVerificationId) {
      setMfaError('La verificación ha caducado. Solicita un nuevo código.');
      return;
    }
    if (mfaExpiresAt && mfaRemainingSeconds === 0) {
      setMfaError('El código ha caducado o ya no es válido. Solicita uno nuevo.');
      return;
    }
    if (!mfaCode) {
      setMfaError('Introduce el código recibido por SMS.');
      return;
    }

    setMfaLoading(true);
    setMfaError('');

    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await mfaResolver.resolveSignIn(assertion);
      resetMfaState();
    } catch (err: any) {
      const code = err?.code || 'unknown';
      if (code === 'auth/invalid-verification-code') {
        console.debug('[MFA] resolveSignIn failed', err);
        setMfaFailedAttempts((prev) => prev + 1);
        setMfaError('El código no es correcto. Revisa el SMS e inténtalo de nuevo.');
      } else if (
        code === 'auth/missing-verification-id' ||
        code === 'auth/invalid-verification-id' ||
        code === 'auth/code-expired' ||
        code === 'auth/session-expired' ||
        code === 'auth/timeout'
      ) {
        console.debug('[MFA] resolveSignIn failed', err);
        setMfaExpiresAt(Date.now());
        setMfaRemainingSeconds(0);
        setMfaError('El código ha caducado o ya no es válido. Solicita uno nuevo.');
      } else if (code === 'auth/too-many-requests') {
        console.debug('[MFA] resolveSignIn failed', err);
        setMfaError('Demasiados intentos. Espera unos minutos e inténtalo de nuevo.');
      } else if (code === 'auth/captcha-check-failed' || code === 'auth/invalid-app-credential') {
        console.debug('[MFA] resolveSignIn failed', err);
        setMfaError('No se pudo verificar el envío del SMS. Recarga la página e inténtalo.');
      } else {
        console.error('[MFA] resolveSignIn failed', err);
        setMfaError('No se pudo completar la verificación. Inténtalo de nuevo.');
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const handleBackToLanding = () => {
    setView('landing');
    resetForm();
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (view === 'register' && !acceptedTerms) {
      setError('Debes aceptar la política y los términos');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (view === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (err: any) {
      const code = err?.code || 'unknown';
      if (code === 'auth/multi-factor-auth-required') {
        console.debug('[MFA] multi-factor-auth-required', err);
        const resolver = getMultiFactorResolver(auth, err);
        await startMfaFlow(resolver);
        return;
      }
      if (code === 'auth/invalid-credential') setError('Email o contraseña incorrectos');
      else if (code === 'auth/email-already-in-use') setError('Este email ya está registrado');
      else if (code === 'auth/weak-password') setError('La contraseña debe tener al menos 6 caracteres');
      else {
        console.error('[AUTH] email/password failed', err);
        setError('Ocurrió un error. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (view === 'register' && !acceptedTerms) {
      setError('Debes aceptar la política y los términos primero');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);
        await startMfaFlow(resolver);
        return;
      }
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        setError('Necesitas completar el popup de Google para continuar.');
      } else {
        setError('Error al iniciar con Google.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = () => {
    setAcceptedTerms(true);
  };

  const isMfaCodeActive = mfaExpiresAt !== null;
  const isMfaCodeExpired = isMfaCodeActive && mfaRemainingSeconds === 0;
  const hasMfaFailedTooManyTimes = mfaFailedAttempts >= MFA_MAX_FAILURES_BEFORE_RESEND;
  const shouldShowResend = isMfaCodeActive && (isMfaCodeExpired || hasMfaFailedTooManyTimes);
  const canResendNow = shouldShowResend && mfaCooldownSeconds === 0 && !!mfaSelectedHint;

  useModalAccessibility(mfaDialogRef, {
    enabled: mfaOpen,
    onClose: mfaLoading ? undefined : resetMfaState
  });

  useModalAccessibility(deleteSuccessDialogRef, {
    enabled: showDeleteSuccess,
    onClose: () => setShowDeleteSuccess(false)
  });

  return (
    <div className="min-h-[100dvh] w-full bg-canvas flex flex-col lg:items-center lg:justify-center lg:p-6 font-sans text-ink overflow-x-hidden relative">

      {showPrivacyModal && <PrivacyModal onClose={() => setShowPrivacyModal(false)} onAccept={handleAcceptTerms} />}
      {showResetModal && <ResetPasswordModal onClose={() => setShowResetModal(false)} defaultEmail={email} />}

      {mfaOpen && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm motion-fade-through-fast">
          <div
            ref={mfaDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Verificacion en dos pasos"
            tabIndex={-1}
            className="bg-panel rounded-2xl shadow-2xl w-full max-w-sm max-h-[90dvh] overflow-y-auto p-6 motion-micro-scale text-center relative"
          >
            <button
              onClick={resetMfaState}
              className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-raised"
              disabled={mfaLoading}
              aria-label="Cerrar verificacion en dos pasos"
            >
              <X size={20} />
            </button>

            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-juris-soft mb-4 border border-line">
              <Lock className="h-6 w-6 text-juris" />
            </div>

            <h3 className="text-xl font-bold text-ink mb-2">Verificación en dos pasos</h3>

            {mfaStep === 'select' ? (
              <>
                <p className="text-muted mb-4 text-sm leading-relaxed">
                  Selecciona el método para recibir el código SMS.
                </p>
                <div className="space-y-2 mb-4">
                  {mfaHints.map((hint) => {
                    const isSelected = mfaSelectedHint?.uid === hint.uid;
                    const label = (hint as { phoneNumber?: string })?.phoneNumber || 'SMS';
                    return (
                      <button
                        key={hint.uid}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => {
                          setMfaSelectedHint(hint);
                          setMfaError('');
                        }}
                        className={`w-full border rounded-xl px-4 py-3 text-sm text-left transition-colors ${isSelected ? 'border-juris bg-juris-soft text-juris' : 'border-line text-ink hover:border-line'}`}
                      >
                        SMS a {label}
                      </button>
                    );
                  })}
                </div>
                {mfaError && (
                  <div role="alert" className="mb-3 rounded-xl bg-danger-soft border border-danger text-danger text-xs p-2">
                    <div>{mfaError}</div>
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={resetMfaState}
                    className="flex-1 min-h-11 py-3 bg-raised text-ink font-bold rounded-xl hover:bg-raised transition-colors"
                    disabled={mfaLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={async () => {
                      await sendMfaCode('initial');
                    }}
                    className="flex-1 min-h-11 py-3 bg-action text-white font-bold rounded-xl hover:bg-action-hover transition-all shadow-lg shadow-black disabled:opacity-50"
                    disabled={mfaLoading || !mfaSelectedHint}
                  >
                    {mfaLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Enviar código'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted mb-4 text-sm leading-relaxed">
                  Hemos enviado un código por SMS a <strong>{maskPhoneNumber(mfaSelectedHint)}</strong>
                </p>
                {isMfaCodeActive && (
                  <div
                    className={`mb-3 rounded-lg px-3 py-2 text-xs font-semibold ${
                      isMfaCodeExpired
                        ? 'bg-danger-soft border border-danger text-danger'
                        : 'bg-canvas border border-line text-muted'
                    }`}
                  >
                    {isMfaCodeExpired ? 'El código ha caducado.' : `Caduca en ${formatCountdown(mfaRemainingSeconds)}`}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Código SMS"
                  aria-label="Codigo SMS"
                  aria-invalid={!!mfaError}
                  aria-describedby={mfaError ? 'login-mfa-code-error' : undefined}
                  value={mfaCode}
                  onChange={(e) => {
                    setMfaCode(e.target.value);
                    setMfaError('');
                  }}
                  className="w-full bg-canvas border border-line text-ink text-sm rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-juris focus:border-transparent mb-3"
                />
                {mfaError && (
                  <div id="login-mfa-code-error" role="alert" className="mb-3 rounded-xl bg-danger-soft border border-danger text-danger text-xs p-2">
                    <div>{mfaError}</div>
                  </div>
                )}
                {shouldShowResend && (
                  <button
                    type="button"
                    onClick={async () => {
                      await sendMfaCode('resend');
                    }}
                    className={`mb-3 text-xs font-semibold ${
                      canResendNow ? 'text-juris hover:underline' : 'text-muted cursor-not-allowed'
                    }`}
                    disabled={mfaLoading || !canResendNow}
                  >
                    {canResendNow
                      ? 'Solicitar un nuevo código'
                      : `Disponible en ${formatCountdown(mfaCooldownSeconds)}`}
                  </button>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={resetMfaState}
                    className="flex-1 min-h-11 py-3 bg-raised text-ink font-bold rounded-xl hover:bg-raised transition-colors"
                    disabled={mfaLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={verifyMfaCode}
                    className="flex-1 min-h-11 py-3 bg-action text-white font-bold rounded-xl hover:bg-action-hover transition-all shadow-lg shadow-black disabled:opacity-50"
                    disabled={mfaLoading || !mfaCode || isMfaCodeExpired}
                  >
                    {mfaLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Verificar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showDeleteSuccess && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm motion-fade-through-fast">
          <div
            ref={deleteSuccessDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Cuenta eliminada"
            tabIndex={-1}
            className="bg-panel rounded-2xl shadow-2xl w-full max-w-sm max-h-[90dvh] overflow-y-auto p-6 motion-micro-scale text-center relative"
          >
            <button onClick={() => setShowDeleteSuccess(false)} className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-raised" aria-label="Cerrar aviso de cuenta eliminada">
              <X size={20} />
            </button>

            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-soft mb-4 border border-success">
              <CheckCircle2 className="h-8 w-8 text-success" />
            </div>

            <h3 className="text-xl font-bold text-ink mb-2">Cuenta Eliminada</h3>
            <p className="text-muted mb-6 text-sm leading-relaxed">
              Tus datos han sido borrados correctamente de nuestros sistemas. Lamentamos verte partir.
            </p>

            <button
              onClick={() => setShowDeleteSuccess(false)}
              className="w-full min-h-11 py-3 bg-action text-white font-bold rounded-xl hover:bg-action-hover transition-all shadow-lg shadow-black"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {view === 'landing' && (
        <div className="flex-1 flex flex-col motion-fade-through min-h-full lg:flex-none lg:h-auto lg:w-full lg:max-w-3xl lg:max-h-[calc(100dvh-3rem)] lg:bg-panel lg:rounded-[28px] lg:shadow-2xl lg:shadow-black/10 lg:border lg:border-line lg:overflow-y-auto lg:overflow-x-hidden no-scrollbar">
          <HeaderCurve isLanding={true} />

          <div className="flex-grow px-6 flex flex-col justify-start pt-12 lg:flex-none lg:pt-8 lg:pb-8 lg:px-10 items-center gap-3 md:gap-4 pb-4 w-full max-w-md lg:max-w-xl mx-auto">
            <div className="text-center space-y-1 mb-2 md:mb-4 lg:mb-3">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-ink">Seguridad jurídica operativa</h3>
              <p className="text-muted text-sm">Respuestas fiables para intervenciones reales</p>
            </div>
            <div className="w-full lg:max-w-md space-y-3">
              <button type="button" onClick={() => setView('register')} className="w-full min-h-11 bg-action text-white py-3.5 rounded-2xl font-bold text-lg shadow-xl shadow-black hover:bg-action-hover hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-300">EMPEZAR GRATIS</button>
              <button type="button" onClick={() => setView('login')} className="w-full min-h-11 bg-panel text-juris border-2 border-juris py-3.5 rounded-2xl font-bold text-lg hover:bg-juris-soft transition-all duration-300">INICIAR SESIÓN</button>
            </div>
            {!isStandalone && <div className="w-full lg:max-w-md mt-2 border-t border-line pt-3 text-center">
              <p className="text-xs text-muted mb-2">También puedes instalar Juris en este dispositivo</p>
              <PwaInstallControl
                label="Instalar Juris"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-muted transition-colors hover:bg-raised hover:text-juris focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2"
              />
            </div>}
          </div>
        </div>
      )}

      {view !== 'landing' && (
        <div className="flex-1 flex flex-col motion-fade-through min-h-full lg:flex-none lg:h-auto lg:w-full lg:max-w-xl lg:max-h-[calc(100dvh-3rem)] lg:bg-panel lg:rounded-[28px] lg:shadow-2xl lg:shadow-black/10 lg:border lg:border-line lg:overflow-y-auto lg:overflow-x-hidden no-scrollbar">
          <HeaderCurve
            isLanding={false}
            showBack={true}
            onBack={handleBackToLanding}
            title={view === 'register' ? 'Crea tu cuenta' : 'Hola de nuevo'}
            subtitle={view === 'register' ? 'Únete a la comunidad de agentes que ya usan Juris' : 'Introduce tus credenciales para acceder'}
          />

          <div className="flex-grow px-8 flex flex-col justify-start pt-10 lg:flex-none lg:pt-7 lg:pb-7 lg:px-10 pb-4 w-full max-w-sm lg:max-w-md mx-auto">
            <form onSubmit={handleAuth} className="space-y-4 w-full">
              <div className="space-y-3">
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-juris transition-colors" size={18} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="Correo electrónico"
                    autoComplete="email"
                    aria-label="Correo electrónico"
                    aria-invalid={!!error}
                    aria-describedby={error ? 'auth-form-error' : undefined}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-canvas border border-line text-ink text-sm rounded-2xl focus:ring-2 focus:ring-juris focus:border-transparent block pl-11 p-3.5 transition-all outline-none hover:bg-raised focus:bg-panel"
                  />
                </div>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-juris transition-colors" size={18} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Contraseña"
                    autoComplete={view === 'login' ? "current-password" : "new-password"}
                    aria-label="Contraseña"
                    aria-invalid={!!error}
                    aria-describedby={error ? 'auth-form-error' : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-canvas border border-line text-ink text-sm rounded-2xl focus:ring-2 focus:ring-juris focus:border-transparent block pl-11 pr-12 p-3.5 transition-all outline-none hover:bg-raised focus:bg-panel"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-muted hover:text-juris transition-colors" aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>

                {view === 'login' && (
                  <div className="text-center pt-1">
                    <button type="button" onClick={() => setShowResetModal(true)} className="text-xs text-juris hover:underline font-medium">
                      ¿Has olvidado tu contraseña?
                    </button>
                  </div>
                )}
              </div>

              {view === 'register' && (
                <div className="flex items-start gap-2 pl-1 mt-1">
                  <div className="relative flex items-center pt-0.5">
                    <input type="checkbox" id="terms" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="peer h-4 w-4 cursor-pointer appearance-none rounded border-2 border-line bg-panel checked:border-juris checked:bg-action transition-all" />
                    <Check className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transform scale-75" size={14} />
                  </div>
                  <label htmlFor="terms" className="text-[11px] text-muted leading-tight cursor-pointer select-none">He leído y acepto la <span onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }} className="text-juris font-bold hover:underline">Política de privacidad y los Términos y condiciones</span> de Juris.</label>
                </div>
              )}

              {error && <div id="auth-form-error" role="alert" className="bg-danger-soft border border-danger text-danger text-xs p-2 rounded-xl text-center font-medium animate-pulse flex items-center justify-center gap-2"><span>⚠️</span> {error}</div>}

              <button type="submit" disabled={loading} className={`w-full min-h-11 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] flex items-center justify-center gap-2 ${loading ? 'bg-action/70 cursor-not-allowed' : 'bg-action hover:bg-action-hover shadow-black'}`}>{loading ? <Loader2 className="animate-spin" size={20} /> : (view === 'register' ? 'EMPEZAR GRATIS' : 'ACCEDER AHORA')}</button>

              <div className="relative py-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-line"></div></div><div className="relative flex justify-center text-xs text-muted bg-panel px-3 font-medium">o continúa con</div></div>
              <button type="button" onClick={handleGoogleLogin} className="w-full min-h-11 bg-panel text-ink border border-line py-3 rounded-xl font-bold hover:bg-canvas hover:border-line transition-all flex items-center justify-center gap-3 text-sm"><img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" /> Google</button>

              <div className="text-center pt-1"><p className="text-xs text-muted">{view === 'register' ? '¿Ya tienes cuenta?' : '¿Aún no tienes cuenta?'}
                {/*
                   CORRECCIÓN AQUÍ: AÑADIDO TYPE="BUTTON"
                   Para evitar que el navegador piense que es un submit y salte la validación
                */}
                <button type="button" onClick={() => { setView(view === 'register' ? 'login' : 'register'); resetForm(); }} className="ml-1 text-juris font-bold hover:underline">{view === 'register' ? 'Inicia sesión' : 'Regístrate'}</button>
              </p></div>
            </form>
            {!isStandalone && <div className="mt-5 border-t border-line pt-3 text-center">
              <PwaInstallControl
                label="Instalar Juris"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-muted transition-colors hover:bg-raised hover:text-juris focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2"
              />
            </div>}
          </div>
        </div>
      )}

      <div key={recaptchaKey} ref={recaptchaContainerRef} className="h-0 w-0 overflow-hidden opacity-0" />
    </div>
  );
};

export default Login;
